import { useState } from "react";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { fillTemplate } from "../templates/fillTemplate";
import {
  deleteTranscriptText,
  giveTranscriptConsent,
  hasActiveTranscriptConsent,
  stopTranscript,
  useTranscriptConsent,
  useTranscriptNotice,
} from "./transcriptConsent";
import { CopilotSuggestionPanel } from "./CopilotSuggestionPanel";
import { CopilotSummaryPanel } from "./CopilotSummaryPanel";
import type { CopilotContext } from "./copilotSuggest";
import { HelpTooltip } from "../help/HelpTooltip";

/**
 * Aviso de transcripción + candado de consentimiento (Bloque C2, paso 2): sin marcar el
 * checkbox no hay forma de arrancar la captura/transcripción (`AudioCaptureCard`, paso 3-4).
 * "Detener" y "Borrar transcripción de esta llamada" cortan la captura en vivo (ver
 * `AudioCaptureCard`, que reacciona a `stopped_at`) y muestran/borran el texto transcrito.
 */
export const TranscriptConsentCard = ({
  dealId,
  contactId,
  companyId,
  callSessionId,
  salesId,
  remitente,
  context = {},
}: {
  dealId: number;
  contactId: number | null;
  companyId: number | null;
  callSessionId: number | null;
  salesId: number | null;
  remitente: string;
  context?: CopilotContext;
}) => {
  const { data: notice } = useTranscriptNotice();
  const { data: consent } = useTranscriptConsent(dealId);
  const active = hasActiveTranscriptConsent(consent);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  if (!notice) return null;
  const texto = fillTemplate(notice.texto, {
    remitente,
    firma: notice.firma,
    enlace_aviso: notice.enlace_aviso,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["transcript_consent", dealId] });

  const onCheck = async (checked: boolean) => {
    if (!checked || active || !contactId || !salesId) return;
    setBusy(true);
    try {
      await giveTranscriptConsent({
        contactId,
        companyId,
        dealId,
        callSessionId,
        salesId,
        noticeText: texto,
        noticeVersion: notice.version,
      });
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };

  const onStop = async () => {
    if (!consent) return;
    setBusy(true);
    try {
      await stopTranscript(consent.id);
      refresh();
      notify("Transcripción detenida", { type: "info" });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };

  const onDelete = async () => {
    if (
      !consent ||
      !window.confirm(
        "¿Borrar el texto de la transcripción de esta llamada? El registro del consentimiento se conserva.",
      )
    )
      return;
    setBusy(true);
    try {
      await deleteTranscriptText(consent.id);
      refresh();
      notify("Transcripción de esta llamada borrada", { type: "success" });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };

  return (
    <Card className="gap-2 p-3 text-sm">
      <b className="inline-flex items-center gap-1">
        Aviso de transcripción
        <HelpTooltip topic="calls.consent" />
      </b>
      <p className="whitespace-pre-line">{texto}</p>
      {notice.enlace_aviso ? (
        <a
          href={notice.enlace_aviso}
          target="_blank"
          rel="noreferrer"
          className="w-fit font-semibold text-primary underline"
        >
          Ver aviso de privacidad
        </a>
      ) : (
        <span className="text-muted-foreground">
          (Falta configurar el enlace del aviso de privacidad en IA y ajustes.)
        </span>
      )}
      {!contactId && (
        <span className="text-[var(--critical)]">
          Esta empresa no tiene contacto: agrégalo en su ficha para poder
          registrar el consentimiento.
        </span>
      )}
      <label className="flex items-start gap-2">
        <Checkbox
          className="mt-0.5"
          checked={active}
          disabled={active || busy || !contactId || !salesId}
          onCheckedChange={onCheck}
        />
        <span>
          <b>
            La persona autorizó en voz, al inicio de esta llamada, que se
            transcriba.
          </b>{" "}
          Le leí el aviso: no se guarda el audio, el texto se borra a los 30
          días, puede pedir que me detenga o que lo borre, y puede consultar el
          aviso de privacidad de Raftel.
        </span>
      </label>
      <p className="text-xs text-muted-foreground">
        Si la persona pide que te detengas, presiona "Detener transcripción". Si
        pide que se borre, presiona "Borrar transcripción de esta llamada". Cada
        llamada nueva necesita un consentimiento nuevo.
      </p>
      {consent && (
        <>
          <p className="text-xs text-muted-foreground">
            Registrado{" "}
            {new Date(consent.given_at).toLocaleTimeString("es-MX", {
              hour: "2-digit",
              minute: "2-digit",
            })}
            {consent.stopped_at && " · transcripción detenida"}
            {consent.transcript_deleted_at && " · transcripción borrada"}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!!consent.stopped_at || busy}
              onClick={onStop}
            >
              Detener transcripción
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={onDelete}
            >
              Borrar transcripción de esta llamada
            </Button>
          </div>
          {consent.transcript_text && (
            <div className="max-h-40 overflow-y-auto rounded-md border bg-secondary p-2 text-sm whitespace-pre-line">
              {consent.transcript_text}
            </div>
          )}
          <CopilotSuggestionPanel
            consentId={consent.id}
            transcript={consent.transcript_text ?? ""}
            context={context}
          />
          {consent.stopped_at && (
            <CopilotSummaryPanel
              consentId={consent.id}
              contactId={contactId}
              salesId={salesId}
              transcript={consent.transcript_text ?? ""}
              context={context}
              onTranscriptCleared={refresh}
            />
          )}
        </>
      )}
    </Card>
  );
};
